import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    Container,
    Box,
    CircularProgress,
    Snackbar,
    Alert,
    Slide,
    Dialog,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import PaidIcon from "@mui/icons-material/Paid";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import GavelIcon from "@mui/icons-material/Gavel";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MailIcon from "@mui/icons-material/Mail";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    getPlatformFeeSettings,
    savePlatformFeeSettings,
    getCommissionSettings,
    saveCommissionSettings,
    sendFinanceSettingsOtp,
} from "../../services/festival";

const SlideTransition = (props) => <Slide {...props} direction="up" />;

export default function FinanceSettings() {
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Section 1: Platform Fee State
    const [platformFeeEnabled, setPlatformFeeEnabled] = useState(true);
    const [platformFeeType, setPlatformFeeType] = useState("percentage"); // 'percentage' | 'fixed'
    const [platformFeeValue, setPlatformFeeValue] = useState("3.0");

    // Section 2: Festival Commission State
    const [commissionEnabled, setCommissionEnabled] = useState(true);
    const [commissionType, setCommissionType] = useState("percentage"); // 'percentage' | 'fixed'
    const [commissionValue, setCommissionValue] = useState("6.0");

    // Section 3: GST Configuration State
    const [gstRate, setGstRate] = useState("0");
    const [sacCode] = useState("998314");
    const defaultEmail = process.env.REACT_APP_ADMIN_FINANCE_EMAIL 
        || (typeof window !== "undefined" && window.location.hostname.includes("freecomers.com") 
            ? "freecomersteam@gmail.com" 
            : "support@autovertest.com");
    const [adminEmail, setAdminEmail] = useState(defaultEmail);

    // OTP Modal State
    const [otpModal, setOtpModal] = useState({
        open: false,
        fieldTitle: "",
        fieldSummary: "",
        targetSection: "", // 'platformFee' | 'commission' | 'gst'
        isSubmitting: false,
        error: "",
        pendingToggle: null, // null | { section: 'platformFee' | 'commission', targetState: boolean }
    });

    // 6-digit OTP input boxes state
    const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
    const [timeLeft, setTimeLeft] = useState(299); // 4:59 (299 seconds)
    const [isResending, setIsResending] = useState(false);
    const digitInputRefs = [
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null),
    ];

    // ─── Countdown Timer for OTP ──────────────────────────────────────────────
    useEffect(() => {
        let timer;
        if (otpModal.open && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        setOtpModal((m) => ({
                            ...m,
                            error: "The OTP code has expired. Please click 'Resend OTP' to receive a fresh code.",
                        }));
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [otpModal.open, timeLeft]);

    const formatTimer = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    // ─── Fetch Settings from API ──────────────────────────────────────────────
    const fetchAllSettings = useCallback(async () => {
        try {
            setLoading(true);
            const [platRes, commRes] = await Promise.allSettled([
                getPlatformFeeSettings(),
                getCommissionSettings(),
            ]);

            if (platRes.status === "fulfilled" && platRes.value?.data) {
                const p = platRes.value.data;
                const isEnabled = p.enabled !== undefined ? Boolean(p.enabled) : (p.is_enabled !== undefined ? Boolean(p.is_enabled) : true);
                setPlatformFeeEnabled(isEnabled);
                setPlatformFeeType(p.type === "fixed" ? "fixed" : "percentage");
                setPlatformFeeValue(String(p.value ?? "3.0"));
                if (p.freecomers_gst_rate !== undefined) {
                    setGstRate(String(p.freecomers_gst_rate ?? "0"));
                }
                if (p.admin_email) {
                    setAdminEmail(p.admin_email);
                }
            }

            if (commRes.status === "fulfilled" && commRes.value?.data) {
                const c = commRes.value.data;
                const isEnabled = c.enabled !== undefined ? Boolean(c.enabled) : (c.is_enabled !== undefined ? Boolean(c.is_enabled) : true);
                setCommissionEnabled(isEnabled);
                setCommissionType(c.type === "fixed" ? "fixed" : "percentage");
                setCommissionValue(String(c.value ?? "6.0"));
                if (c.admin_email) {
                    setAdminEmail(prev => prev || c.admin_email);
                }
            }
        } catch (err) {
            console.error("Error loading finance settings:", err);
            setSnackbar({ open: true, message: "Failed to load global finance settings", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllSettings();
    }, [fetchAllSettings]);

    // ─── Toggle Handlers (Requires OTP Verification) ─────────────────────────
    const handleOpenToggleOtpModal = (section, targetState) => {
        const isPF = section === "platformFee";
        const fieldTitle = targetState
            ? (isPF ? "Enable Platform Fee" : "Enable Freecomers Fee")
            : (isPF ? "Disable Platform Fee" : "Disable Freecomers Fee");
        const fieldSummary = targetState
            ? (isPF ? "Enabling platform fee will charge filmmakers during checkout." : "Enabling Freecomers fee will retain Freecomers fee when splitting payouts.")
            : (isPF ? "Disabling platform fee will waive platform fees for filmmakers during checkout." : "Disabling Freecomers fee will transfer 100% of the submission base fee to the festival.");

        setOtpDigits(["", "", "", "", "", ""]);
        setTimeLeft(299);
        setOtpModal({
            open: true,
            targetSection: section,
            fieldTitle,
            fieldSummary,
            isSubmitting: false,
            error: "",
            pendingToggle: { section, targetState },
        });

        setTimeout(() => {
            if (digitInputRefs[0]?.current) {
                digitInputRefs[0].current.focus();
            }
        }, 50);

        sendFinanceSettingsOtp(section)
            .then((res) => {
                const email = res?.recipient_email || res?.data?.recipient_email || res?.admin_email || res?.data?.admin_email;
                if (email) {
                    setAdminEmail(email);
                }
            })
            .catch((e) => {
                console.error("OTP dispatch error:", e);
                const errMsg = e.response?.data?.message || (e.response?.status === 404 ? "OTP API route not found (404). Please ensure the backend release is updated." : "Failed to dispatch verification OTP. Please try again.");
                setOtpModal(prev => ({
                    ...prev,
                    error: errMsg,
                }));
                setSnackbar({
                    open: true,
                    message: errMsg,
                    severity: "error",
                });
            });
    };

    // ─── Live Preview Calculator ──────────────────────────────────────────────
    const preview = useMemo(() => {
        const base = 1000.0;
        const pVal = Number(platformFeeValue) || 0;
        const cVal = Number(commissionValue) || 0;
        const gVal = Number(gstRate) || 0;

        const pFee = platformFeeEnabled
            ? platformFeeType === "percentage" ? (base * pVal) / 100 : pVal
            : 0;

        const pGst = (pFee * gVal) / 100;
        const checkoutPreGateway = base + pFee + pGst;
        const gatewayFee = (checkoutPreGateway * 0.03 * 1.18);
        const totalFilmmakerPays = checkoutPreGateway + gatewayFee;

        const commission = commissionEnabled
            ? commissionType === "percentage" ? (base * cVal) / 100 : cVal
            : 0;

        const routeBase = Math.max(0, base - commission);
        const routeFee = Number((routeBase * 0.0025 * 1.18).toFixed(2));
        const netFestivalSettled = Math.max(0, routeBase - routeFee);

        return {
            base: base.toFixed(2),
            pFee: pFee.toFixed(2),
            pGst: pGst.toFixed(2),
            gatewayFee: gatewayFee.toFixed(2),
            totalFilmmakerPays: totalFilmmakerPays.toFixed(2),
            commission: commission.toFixed(2),
            routeFee: routeFee.toFixed(2),
            netFestivalSettled: netFestivalSettled.toFixed(2),
        };
    }, [platformFeeEnabled, platformFeeType, platformFeeValue, commissionEnabled, commissionType, commissionValue, gstRate]);

    // ─── Open OTP Verification Modal ──────────────────────────────────────────
    const handleOpenOtpModal = (section, fieldTitle, fieldSummary) => {
        // 1. Open modal INSTANTLY (0ms delay)
        setOtpDigits(["", "", "", "", "", ""]);
        setTimeLeft(299);
        setOtpModal({
            open: true,
            targetSection: section,
            fieldTitle,
            fieldSummary,
            isSubmitting: false,
            error: "",
            pendingToggle: null,
        });

        // 2. Focus first box immediately
        setTimeout(() => {
            if (digitInputRefs[0]?.current) {
                digitInputRefs[0].current.focus();
            }
        }, 50);

        // 3. Dispatch OTP via API in background without blocking modal
        sendFinanceSettingsOtp(section)
            .then((res) => {
                const email = res?.recipient_email || res?.data?.recipient_email || res?.admin_email || res?.data?.admin_email;
                if (email) {
                    setAdminEmail(email);
                }
            })
            .catch((e) => {
                console.error("OTP dispatch error:", e);
                const errMsg = e.response?.data?.message || (e.response?.status === 404 ? "OTP API route not found (404). Please ensure the backend release is updated." : "Failed to dispatch verification OTP. Please try again.");
                setOtpModal(prev => ({
                    ...prev,
                    error: errMsg,
                }));
                setSnackbar({
                    open: true,
                    message: errMsg,
                    severity: "error",
                });
            });
    };

    // ─── Handle OTP Digit Change & Auto-Advance ───────────────────────────────
    const handleDigitChange = (index, value) => {
        const cleanVal = value.replace(/[^0-9]/g, "").slice(-1);
        const newDigits = [...otpDigits];
        newDigits[index] = cleanVal;
        setOtpDigits(newDigits);

        if (cleanVal && index < 5 && digitInputRefs[index + 1]?.current) {
            digitInputRefs[index + 1].current.focus();
            digitInputRefs[index + 1].current.select();
        }
    };

    const handleDigitKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otpDigits[index] && index > 0 && digitInputRefs[index - 1]?.current) {
            digitInputRefs[index - 1].current.focus();
            digitInputRefs[index - 1].current.select();
        }
    };

    const handlePasteOtp = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);
        if (pasted.length > 0) {
            const newDigits = [...otpDigits];
            for (let i = 0; i < 6; i++) {
                newDigits[i] = pasted[i] || "";
            }
            setOtpDigits(newDigits);
            const nextFocus = Math.min(pasted.length, 5);
            if (digitInputRefs[nextFocus]?.current) {
                digitInputRefs[nextFocus].current.focus();
            }
        }
    };

    const handleResendOtp = async () => {
        if (isResending) return;
        setIsResending(true);

        // Immediate visual response for the user
        setTimeLeft(299);
        setOtpDigits(["", "", "", "", "", ""]);
        setOtpModal(prev => ({ ...prev, error: "" }));
        if (digitInputRefs[0]?.current) {
            digitInputRefs[0].current.focus();
        }

        try {
            const section = otpModal.pendingToggle ? otpModal.pendingToggle.section : otpModal.targetSection;
            const res = await sendFinanceSettingsOtp(section);
            const email = res?.recipient_email || res?.data?.recipient_email || res?.admin_email || res?.data?.admin_email || adminEmail;
            if (email) {
                setAdminEmail(email);
            }
            setSnackbar({ open: true, message: `A fresh 6-digit OTP code has been dispatched to ${email || "the super-admin email"}`, severity: "info" });
        } catch (e) {
            const errMsg = e.response?.data?.message || (e.response?.status === 404 ? "OTP API route not found (404)." : "Failed to resend OTP");
            setOtpModal(prev => ({ ...prev, error: errMsg }));
            setSnackbar({ open: true, message: errMsg, severity: "error" });
        } finally {
            setIsResending(false);
        }
    };

    // ─── Confirm OTP and Save ─────────────────────────────────────────────────
    const handleVerifyOtpAndSave = async () => {
        if (timeLeft <= 0) {
            setOtpModal(prev => ({
                ...prev,
                error: "The OTP code has expired. Please click 'Resend OTP' to receive a fresh code."
            }));
            return;
        }
        const fullOtp = otpDigits.join("");
        if (fullOtp.length < 6) {
            setOtpModal(prev => ({ ...prev, error: "Please enter all 6 digits of the OTP passcode" }));
            return;
        }

        try {
            setOtpModal(prev => ({ ...prev, isSubmitting: true, error: "" }));

            if (otpModal.pendingToggle) {
                const { section, targetState } = otpModal.pendingToggle;
                if (section === "platformFee") {
                    await savePlatformFeeSettings({
                        enabled: targetState,
                        is_enabled: targetState,
                        type: platformFeeType,
                        value: Number(platformFeeValue),
                        otp: fullOtp,
                    });
                    setPlatformFeeEnabled(targetState);
                    setSnackbar({
                        open: true,
                        message: `Platform Fee ${targetState ? "enabled" : "disabled"} successfully!`,
                        severity: "success",
                    });
                } else if (section === "commission") {
                    await saveCommissionSettings({
                        enabled: targetState,
                        is_enabled: targetState,
                        type: commissionType,
                        value: Number(commissionValue),
                        otp: fullOtp,
                    });
                    setCommissionEnabled(targetState);
                    setSnackbar({
                        open: true,
                        message: `Freecomers Fee ${targetState ? "enabled" : "disabled"} successfully!`,
                        severity: "success",
                    });
                }
            } else if (otpModal.targetSection === "platformFee") {
                await savePlatformFeeSettings({
                    enabled: platformFeeEnabled,
                    is_enabled: platformFeeEnabled,
                    type: platformFeeType,
                    value: Number(platformFeeValue),
                    otp: fullOtp,
                });
                setSnackbar({ open: true, message: "Global Platform Fee updated successfully!", severity: "success" });
            } else if (otpModal.targetSection === "commission") {
                await saveCommissionSettings({
                    enabled: commissionEnabled,
                    is_enabled: commissionEnabled,
                    type: commissionType,
                    value: Number(commissionValue),
                    otp: fullOtp,
                });
                setSnackbar({ open: true, message: "Global Freecomers Fee updated successfully!", severity: "success" });
            } else if (otpModal.targetSection === "gst") {
                await savePlatformFeeSettings({
                    freecomers_gst_rate: Number(gstRate),
                    otp: fullOtp,
                });
                setSnackbar({ open: true, message: "Global GST Configuration updated successfully!", severity: "success" });
            }

            setOtpModal(prev => ({ ...prev, open: false, pendingToggle: null }));
            fetchAllSettings();
        } catch (err) {
            console.error("Failed to save settings:", err);
            setOtpModal(prev => ({ ...prev, error: err?.response?.data?.message || "Invalid OTP code. Please check your email and try again." }));
        } finally {
            setOtpModal(prev => ({ ...prev, isSubmitting: false }));
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                <CircularProgress size={36} sx={{ color: "#e90000" }} />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3, mb: 10, px: { xs: 1.5, sm: 3 } }}>
            <style>{`
                input[type=number]::-webkit-inner-spin-button,
                input[type=number]::-webkit-outer-spin-button {
                    -webkit-appearance: none !important;
                    margin: 0 !important;
                }
                input[type=number] {
                    -moz-appearance: textfield !important;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                @media (max-width: 640px) {
                    .finance-input-row {
                        flex-direction: column !important;
                        align-items: stretch !important;
                    }
                    .finance-action-btn {
                        width: 100% !important;
                        justify-content: center !important;
                        margin-top: 8px !important;
                    }
                    .finance-radio-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .finance-grid-2col {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>

            {/* ── Page Header ── */}
            <div style={{ marginBottom: "20px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: 800, color: "#101828", margin: 0, marginBottom: "4px" }}>
                    Finance & Tax Settings
                </h1>
                <div style={{ fontSize: "13px", color: "#667085" }}>
                    Configure the global platform fee, Freecomers Fee rate, and tax rules applied across all active festivals.
                </div>
            </div>

            {/* ── Security Notice Banner ── */}
            <div
                style={{
                    background: "#fffbeb",
                    border: "1px solid #fde047",
                    borderRadius: "10px",
                    padding: "14px 18px",
                    marginBottom: "24px",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "14px",
                }}
            >
                <div
                    style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "#fef3c7",
                        color: "#b45309",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        flexShrink: 0,
                    }}
                >
                    <ShieldIcon sx={{ fontSize: 20 }} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: "13px", color: "#92400e" }}>
                        Critical Security Rule: 2-Factor Email OTP Required for Global Changes
                    </div>
                    <div style={{ fontSize: "12px", color: "#b45309", marginTop: "3px", lineHeight: 1.4 }}>
                        Global financial settings directly govern checkout calculations and Razorpay Route settlements platform-wide. Modifying any global field triggers a mandatory 6-digit OTP sent to the super-admin email (<strong>{adminEmail || defaultEmail}</strong>).
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 1: PLATFORM FEE
               ═══════════════════════════════════════════════════════════════════ */}
            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #eaecf0",
                    borderRadius: "16px",
                    marginBottom: "24px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: "16px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        borderBottom: "1px solid #f2f4f7",
                        background: "#faf8f5",
                    }}
                >
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            background: "#fee4e2",
                            color: "#e90000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                        }}
                    >
                        <CurrencyRupeeIcon sx={{ fontSize: 20 }} />
                    </div>
                    <div>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#101828" }}>Platform Fee</div>
                        <div style={{ fontSize: "12px", color: "#667085" }}>
                            Set the global default platform fee charged per submission to filmmakers
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "clamp(16px, 3vw, 24px)" }}>
                    {/* Toggle */}
                    <div
                        style={{
                            background: "#faf9f6",
                            border: "1px solid #f2f4f7",
                            borderRadius: "12px",
                            padding: "14px 18px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "20px",
                        }}
                    >
                        <div>
                            <div style={{ fontSize: "13px", fontWeight: 700, color: "#101828", marginBottom: "2px" }}>
                                Enable Platform Fee
                            </div>
                            <div style={{ fontSize: "12px", color: "#667085" }}>
                                {platformFeeEnabled
                                    ? "Platform fee is enabled. Filmmakers will be charged this fee during checkout."
                                    : "Platform fee is disabled. Filmmakers will not be charged this fee during checkout."}
                            </div>
                        </div>
                        <label style={{ position: "relative", display: "inline-block", width: "44px", height: "24px", flexShrink: 0 }}>
                            <input
                                type="checkbox"
                                checked={platformFeeEnabled}
                                onChange={() => handleOpenToggleOtpModal("platformFee", !platformFeeEnabled)}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    cursor: "pointer",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: platformFeeEnabled ? "#e90000" : "#d0d5dd",
                                    transition: ".3s",
                                    borderRadius: "24px",
                                }}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        content: '""',
                                        height: "18px",
                                        width: "18px",
                                        left: "3px",
                                        bottom: "3px",
                                        backgroundColor: "white",
                                        transition: ".3s",
                                        borderRadius: "50%",
                                        transform: platformFeeEnabled ? "translateX(20px)" : "none",
                                    }}
                                />
                            </span>
                        </label>
                    </div>

                    {/* Radio Group */}
                    <div style={{ fontSize: "12px", fontWeight: 600, color: platformFeeEnabled ? "#344054" : "#98a2b3", marginBottom: "6px" }}>Fee Type</div>
                    <div className="finance-radio-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "16px" }}>
                        <div
                            onClick={() => platformFeeEnabled && setPlatformFeeType("fixed")}
                            style={{
                                border: platformFeeEnabled && platformFeeType === "fixed" ? "1px solid #e90000" : "1px solid #d0d5dd",
                                background: !platformFeeEnabled ? "#f2f4f7" : (platformFeeType === "fixed" ? "#fffbfa" : "#ffffff"),
                                borderRadius: "10px",
                                padding: "10px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                cursor: platformFeeEnabled ? "pointer" : "not-allowed",
                                opacity: platformFeeEnabled ? 1 : 0.6,
                                pointerEvents: platformFeeEnabled ? "auto" : "none",
                                transition: "all 0.2s",
                            }}
                        >
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    border: platformFeeEnabled && platformFeeType === "fixed" ? "2px solid #e90000" : "2px solid #d0d5dd",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {platformFeeType === "fixed" && (
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: platformFeeEnabled ? "#e90000" : "#98a2b3" }} />
                                )}
                            </div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: platformFeeEnabled && platformFeeType === "fixed" ? "#e90000" : (platformFeeEnabled ? "#344054" : "#98a2b3") }}>
                                Fixed Amount (₹)
                            </div>
                        </div>

                        <div
                            onClick={() => platformFeeEnabled && setPlatformFeeType("percentage")}
                            style={{
                                border: platformFeeEnabled && platformFeeType === "percentage" ? "1px solid #e90000" : "1px solid #d0d5dd",
                                background: !platformFeeEnabled ? "#f2f4f7" : (platformFeeType === "percentage" ? "#fffbfa" : "#ffffff"),
                                borderRadius: "10px",
                                padding: "10px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                cursor: platformFeeEnabled ? "pointer" : "not-allowed",
                                opacity: platformFeeEnabled ? 1 : 0.6,
                                pointerEvents: platformFeeEnabled ? "auto" : "none",
                                transition: "all 0.2s",
                            }}
                        >
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    border: platformFeeEnabled && platformFeeType === "percentage" ? "2px solid #e90000" : "2px solid #d0d5dd",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {platformFeeType === "percentage" && (
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: platformFeeEnabled ? "#e90000" : "#98a2b3" }} />
                                )}
                            </div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: platformFeeEnabled && platformFeeType === "percentage" ? "#e90000" : (platformFeeEnabled ? "#344054" : "#98a2b3") }}>
                                Percentage (%)
                            </div>
                        </div>
                    </div>

                    {/* Input + Button Row */}
                    <div className="finance-input-row" style={{ display: "flex", gap: "14px", alignItems: "flex-start", marginBottom: "8px" }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <input
                                    type="number"
                                    disabled={!platformFeeEnabled}
                                    value={platformFeeValue}
                                    onChange={(e) => setPlatformFeeValue(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "10px 14px",
                                        paddingRight: "36px",
                                        border: "1px solid #d0d5dd",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: platformFeeEnabled ? "#101828" : "#98a2b3",
                                        background: platformFeeEnabled ? "#ffffff" : "#f2f4f7",
                                        cursor: platformFeeEnabled ? "text" : "not-allowed",
                                        outline: "none",
                                        boxSizing: "border-box",
                                    }}
                                />
                                <span style={{ position: "absolute", right: "14px", fontSize: "13px", color: platformFeeEnabled ? "#667085" : "#98a2b3", fontWeight: 600 }}>
                                    {platformFeeType === "fixed" ? "₹" : "%"}
                                </span>
                            </div>
                            <div style={{ fontSize: "11px", color: platformFeeEnabled ? "#667085" : "#98a2b3", marginTop: "4px" }}>
                                {platformFeeEnabled
                                    ? `Global default: Filmmakers will be charged ${platformFeeValue}${platformFeeType === "fixed" ? "₹" : "%"} of the submission fee`
                                    : `Platform fee is currently disabled. Enable platform fee above to charge filmmakers during checkout.`}
                            </div>
                        </div>
                        <button
                            className="finance-action-btn"
                            type="button"
                            disabled={!platformFeeEnabled}
                            onClick={() => platformFeeEnabled && handleOpenOtpModal("platformFee", "Global Platform Fee", `${platformFeeValue}${platformFeeType === "fixed" ? "₹" : "%"}`)}
                            style={{
                                background: platformFeeEnabled ? "#e90000" : "#98a2b3",
                                color: "#ffffff",
                                border: "none",
                                padding: "10px 22px",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 700,
                                cursor: platformFeeEnabled ? "pointer" : "not-allowed",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                whiteSpace: "nowrap",
                                transition: "all 0.2s",
                                opacity: platformFeeEnabled ? 1 : 0.6,
                            }}
                        >
                            <ShieldIcon sx={{ fontSize: 16 }} /> Update Fee (Requires OTP)
                        </button>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 2: FESTIVAL COMMISSION FEE
               ═══════════════════════════════════════════════════════════════════ */}
            <div
                style={{
                    background: "#ffffff",
                    border: "2px solid #bbf7d0",
                    borderRadius: "16px",
                    marginBottom: "24px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: "16px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        borderBottom: "1px solid #f2f4f7",
                        background: "#f0fdf4",
                    }}
                >
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            background: "#dcfce7",
                            color: "#16a34a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                        }}
                    >
                        <PaidIcon sx={{ fontSize: 20 }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#101828" }}>Freecomers Fee</div>
                        <div style={{ fontSize: "12px", color: "#667085" }}>
                            Set the global default Freecomers fee deducted from festival submission payouts
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "clamp(16px, 3vw, 24px)" }}>
                    {/* Toggle */}
                    <div
                        style={{
                            background: "#faf9f6",
                            border: "1px solid #f2f4f7",
                            borderRadius: "12px",
                            padding: "14px 18px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "20px",
                        }}
                    >
                        <div>
                            <div style={{ fontSize: "13px", fontWeight: 700, color: "#101828", marginBottom: "2px" }}>
                                Enable Freecomers Fee
                            </div>
                            <div style={{ fontSize: "12px", color: "#667085" }}>
                                {commissionEnabled
                                    ? "Freecomers retains this fee percentage when splitting payouts via Razorpay Route."
                                    : "Freecomers fee is disabled. Full payout base fee is transferred to festival."}
                            </div>
                        </div>
                        <label style={{ position: "relative", display: "inline-block", width: "44px", height: "24px", flexShrink: 0 }}>
                            <input
                                type="checkbox"
                                checked={commissionEnabled}
                                onChange={() => handleOpenToggleOtpModal("commission", !commissionEnabled)}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                            <span
                                style={{
                                    position: "absolute",
                                    cursor: "pointer",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: commissionEnabled ? "#16a34a" : "#d0d5dd",
                                    transition: ".3s",
                                    borderRadius: "24px",
                                }}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        content: '""',
                                        height: "18px",
                                        width: "18px",
                                        left: "3px",
                                        bottom: "3px",
                                        backgroundColor: "white",
                                        transition: ".3s",
                                        borderRadius: "50%",
                                        transform: commissionEnabled ? "translateX(20px)" : "none",
                                    }}
                                />
                            </span>
                        </label>
                    </div>

                    {/* Radio Group */}
                    <div style={{ fontSize: "12px", fontWeight: 600, color: commissionEnabled ? "#344054" : "#98a2b3", marginBottom: "6px" }}>Freecomers Fee Type</div>
                    <div className="finance-radio-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "16px" }}>
                        <div
                            onClick={() => commissionEnabled && setCommissionType("fixed")}
                            style={{
                                border: commissionEnabled && commissionType === "fixed" ? "1px solid #16a34a" : "1px solid #d0d5dd",
                                background: !commissionEnabled ? "#f2f4f7" : (commissionType === "fixed" ? "#f0fdf4" : "#ffffff"),
                                borderRadius: "10px",
                                padding: "10px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                cursor: commissionEnabled ? "pointer" : "not-allowed",
                                opacity: commissionEnabled ? 1 : 0.6,
                                pointerEvents: commissionEnabled ? "auto" : "none",
                                transition: "all 0.2s",
                            }}
                        >
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    border: commissionEnabled && commissionType === "fixed" ? "2px solid #16a34a" : "2px solid #d0d5dd",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {commissionType === "fixed" && (
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: commissionEnabled ? "#16a34a" : "#98a2b3" }} />
                                )}
                            </div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: commissionEnabled && commissionType === "fixed" ? "#16a34a" : (commissionEnabled ? "#344054" : "#98a2b3") }}>
                                Fixed Amount (₹)
                            </div>
                        </div>

                        <div
                            onClick={() => commissionEnabled && setCommissionType("percentage")}
                            style={{
                                border: commissionType === "percentage" && commissionEnabled ? "1px solid #16a34a" : "1px solid #d0d5dd",
                                background: !commissionEnabled ? "#f2f4f7" : (commissionType === "percentage" ? "#f0fdf4" : "#ffffff"),
                                borderRadius: "10px",
                                padding: "10px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                cursor: commissionEnabled ? "pointer" : "not-allowed",
                                opacity: commissionEnabled ? 1 : 0.6,
                                pointerEvents: commissionEnabled ? "auto" : "none",
                                transition: "all 0.2s",
                            }}
                        >
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    border: commissionType === "percentage" ? "2px solid #16a34a" : "2px solid #d0d5dd",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {commissionType === "percentage" && (
                                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: commissionEnabled ? "#16a34a" : "#98a2b3" }} />
                                )}
                            </div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: commissionEnabled && commissionType === "percentage" ? "#16a34a" : (commissionEnabled ? "#344054" : "#98a2b3") }}>
                                Percentage (%)
                            </div>
                        </div>
                    </div>

                    {/* Input + Button Row */}
                    <div className="finance-input-row" style={{ display: "flex", gap: "14px", alignItems: "flex-start", marginBottom: "8px" }}>
                        <div style={{ flex: 1 }}>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <input
                                    type="number"
                                    disabled={!commissionEnabled}
                                    value={commissionValue}
                                    onChange={(e) => setCommissionValue(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "10px 14px",
                                        paddingRight: "36px",
                                        border: commissionEnabled ? "1px solid #86efac" : "1px solid #d0d5dd",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: commissionEnabled ? "#101828" : "#98a2b3",
                                        background: commissionEnabled ? "#ffffff" : "#f2f4f7",
                                        cursor: commissionEnabled ? "text" : "not-allowed",
                                        outline: "none",
                                        boxSizing: "border-box",
                                    }}
                                />
                                <span style={{ position: "absolute", right: "14px", fontSize: "13px", color: commissionEnabled ? "#667085" : "#98a2b3", fontWeight: 600 }}>
                                    {commissionType === "fixed" ? "₹" : "%"}
                                </span>
                            </div>
                            <div style={{ fontSize: "11px", color: commissionEnabled ? "#16a34a" : "#98a2b3", fontWeight: 600, marginTop: "4px" }}>
                                {commissionEnabled
                                    ? `Global default: Freecomers will retain ${commissionValue}${commissionType === "fixed" ? "₹" : "%"} of festival submission fee upon payout`
                                    : `Freecomers fee is currently disabled. 100% of payout base fee is transferred to festival.`}
                            </div>
                        </div>
                        <button
                            className="finance-action-btn"
                            type="button"
                            disabled={!commissionEnabled}
                            onClick={() => commissionEnabled && handleOpenOtpModal("commission", "Global Freecomers Fee", `${commissionValue}${commissionType === "fixed" ? "₹" : "%"}`)}
                            style={{
                                background: commissionEnabled ? "#16a34a" : "#98a2b3",
                                color: "#ffffff",
                                border: "none",
                                padding: "10px 22px",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 700,
                                cursor: commissionEnabled ? "pointer" : "not-allowed",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                whiteSpace: "nowrap",
                                transition: "all 0.2s",
                                opacity: commissionEnabled ? 1 : 0.6,
                            }}
                        >
                            <ShieldIcon sx={{ fontSize: 16 }} /> Update Freecomers Fee (Requires OTP)
                        </button>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 3: GST CONFIGURATION & LIVE PREVIEW
               ═══════════════════════════════════════════════════════════════════ */}
            <div
                style={{
                    background: "#ffffff",
                    border: "1px solid #eaecf0",
                    borderRadius: "16px",
                    marginBottom: "24px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: "16px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        borderBottom: "1px solid #f2f4f7",
                        background: "#faf8f5",
                    }}
                >
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            background: "#fef3c7",
                            color: "#d97706",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                        }}
                    >
                        <GavelIcon sx={{ fontSize: 20 }} />
                    </div>
                    <div>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#101828" }}>GST Configuration</div>
                        <div style={{ fontSize: "12px", color: "#667085" }}>
                            Global tax rate applied to platform fee and Freecomers fee across all festivals
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "clamp(16px, 3vw, 24px)" }}>
                    {/* Blue Info Notice */}
                    <div
                        style={{
                            background: "#eff8ff",
                            border: "1px solid #b2ddff",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            fontSize: "13px",
                            color: "#175cd3",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginBottom: "20px",
                        }}
                    >
                        <InfoIcon sx={{ fontSize: 18, color: "#175cd3" }} />
                        <div>GST is applied <strong>only</strong> on Freecomers Platform Fee and Freecomers Fee.</div>
                    </div>

                    {/* Form 2 Columns: GST Rate + SAC Code */}
                    <div className="finance-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
                        <div>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#344054", marginBottom: "6px", display: "block" }}>
                                GST Rate
                            </label>
                            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                <input
                                    type="number"
                                    value={gstRate}
                                    onChange={(e) => setGstRate(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "10px 14px",
                                        paddingRight: "36px",
                                        border: "1px solid #d0d5dd",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#101828",
                                        background: "#ffffff",
                                        outline: "none",
                                        boxSizing: "border-box",
                                    }}
                                />
                                <span style={{ position: "absolute", right: "14px", fontSize: "13px", color: "#667085", fontWeight: 600 }}>%</span>
                            </div>
                            <div style={{ fontSize: "11px", color: "#d97706", fontWeight: 600, marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                                <WarningAmberIcon sx={{ fontSize: 14 }} /> Freecomers GST = 0% reflects current non-registered status. Can be set to 18% when registered.
                            </div>
                        </div>

                        <div>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#344054", marginBottom: "6px", display: "block" }}>
                                SAC Code
                            </label>
                            <input
                                type="text"
                                value={sacCode}
                                disabled
                                style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    border: "1px solid #d0d5dd",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#475467",
                                    background: "#f2f4f7",
                                    outline: "none",
                                    cursor: "not-allowed",
                                    boxSizing: "border-box",
                                }}
                            />
                            <div style={{ fontSize: "11px", color: "#667085", marginTop: "4px" }}>
                                Services Accounting Code printed on tax invoices
                            </div>
                        </div>
                    </div>

                    {/* Update GST Button */}
                    <div style={{ marginBottom: "20px" }}>
                        <button
                            className="finance-action-btn"
                            type="button"
                            onClick={() => handleOpenOtpModal("gst", "Global GST Tax Rule", `${gstRate}%`)}
                            style={{
                                background: "#d97706",
                                color: "#ffffff",
                                border: "none",
                                padding: "10px 22px",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                transition: "all 0.2s",
                            }}
                        >
                            <ShieldIcon sx={{ fontSize: 16 }} /> Update GST Configuration (Requires OTP)
                        </button>
                    </div>

                    {/* Live Preview Breakdown Box */}
                    <div
                        style={{
                            background: "#fdfbf7",
                            border: "1px solid #ede8de",
                            borderRadius: "12px",
                            padding: "20px",
                            fontSize: "13px",
                        }}
                    >
                        <div style={{ fontSize: "11px", fontWeight: 800, color: "#667085", textTransform: "uppercase", marginBottom: "12px" }}>
                            LIVE PREVIEW — CHECKOUT & SETTLEMENT BREAKDOWN (₹1,000 SUBMISSION)
                        </div>

                        {/* 1. Filmmaker Checkout Side */}
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#175cd3", marginBottom: "4px", textTransform: "uppercase" }}>
                            1. Filmmaker Checkout Side:
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>Submission Fee (Festival)</span>
                            <strong>₹{preview.base}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>Platform Fee ({platformFeeValue}{platformFeeType === "fixed" ? "₹" : "%"})</span>
                            <strong>₹{preview.pFee}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>GST on Platform Fee ({gstRate}%)</span>
                            <strong>₹{preview.pGst}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>Payment Gateway Fee (3% + 18% GST)</span>
                            <strong style={{ color: "#ea580c" }}>₹{preview.gatewayFee}</strong>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "14px",
                                fontWeight: 800,
                                color: "#e90000",
                                borderTop: "1px solid #e5e0d8",
                                paddingTop: "6px",
                                marginTop: "6px",
                            }}
                        >
                            <span>Total Filmmaker Pays</span>
                            <span>₹{preview.totalFilmmakerPays}</span>
                        </div>

                        {/* 2. Festival Settlement Side */}
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#16a34a", margin: "14px 0 4px", textTransform: "uppercase" }}>
                            2. Festival Settlement Side:
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>Base Festival Fee</span>
                            <strong>₹{preview.base}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>Freecomers Fee ({commissionValue}{commissionType === "fixed" ? "₹" : "%"})</span>
                            <strong style={{ color: "#dc2626" }}>-₹{preview.commission}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "3px 0" }}>
                            <span>Razorpay Route Fee (0.25% + 18% GST)</span>
                            <strong style={{ color: "#dc2626" }}>-₹{preview.routeFee}</strong>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "14px",
                                fontWeight: 800,
                                color: "#16a34a",
                                borderTop: "1px solid #bbf7d0",
                                paddingTop: "6px",
                                marginTop: "6px",
                            }}
                        >
                            <span>Net Paid to Festival Bank</span>
                            <span>₹{preview.netFestivalSettled}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SUPER-ADMIN OTP VERIFICATION DIALOG (MATCHING SCREENSHOT 1:1)
               ═══════════════════════════════════════════════════════════════════ */}
            <Dialog
                open={otpModal.open}
                onClose={() => setOtpModal(prev => ({ ...prev, open: false, pendingToggle: null, error: "" }))}
                PaperProps={{
                    sx: {
                        borderRadius: "24px",
                        p: { xs: "24px 16px 20px", sm: "36px 32px 28px" },
                        maxWidth: 480,
                        width: "90%",
                        textAlign: "center",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    },
                }}
            >
                {/* Red Circular Shield Icon */}
                <div
                    style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        background: "#fee4e2",
                        color: "#e90000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                    }}
                >
                    <ShieldIcon sx={{ fontSize: 32 }} />
                </div>

                {/* Modal Title */}
                <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#101828", margin: 0, marginBottom: "8px" }}>
                    Super-Admin OTP Verification
                </h2>

                {/* Subtitle / Description */}
                <div style={{ fontSize: "14px", color: "#667085", lineHeight: "1.5", marginBottom: "6px" }}>
                    You are modifying a global financial configuration: <strong style={{ color: "#101828" }}>{otpModal.fieldTitle} ({otpModal.fieldSummary})</strong>. Enter the 6-digit one-time passcode sent to:
                </div>

                {/* Target Email with Icon */}
                <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", fontWeight: 700, color: "#0284c7", fontSize: "15px", marginBottom: "22px" }}>
                    <MailIcon sx={{ fontSize: 18 }} /> {adminEmail || defaultEmail}
                </div>

                {/* 6 Digit Input Boxes */}
                <div
                    onPaste={handlePasteOtp}
                    style={{
                        display: "flex",
                        gap: "10px",
                        justifyContent: "center",
                        marginBottom: "16px",
                    }}
                >
                    {otpDigits.map((digit, idx) => (
                        <input
                            key={idx}
                            ref={digitInputRefs[idx]}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleDigitChange(idx, e.target.value)}
                            onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                            style={{
                                width: "clamp(36px, 9.5vw, 48px)",
                                height: "clamp(46px, 11.5vw, 56px)",
                                textAlign: "center",
                                fontSize: "clamp(18px, 5vw, 24px)",
                                fontWeight: 800,
                                color: "#0f172a",
                                border: idx === 0 && digit ? "2px solid #ea384c" : "2px solid #e2e8f0",
                                borderRadius: "12px",
                                outline: "none",
                                background: "#ffffff",
                                transition: "all 0.2s",
                                boxSizing: "border-box",
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = "#e90000";
                                e.target.style.boxShadow = "0 0 0 3px rgba(233, 0, 0, 0.15)";
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = "#e2e8f0";
                                e.target.style.boxShadow = "none";
                            }}
                        />
                    ))}
                </div>

                {/* Error if any */}
                {otpModal.error && (
                    <div style={{ color: "#dc2626", fontSize: "12px", fontWeight: 600, marginBottom: "12px" }}>
                        {otpModal.error}
                    </div>
                )}

                {/* Timer and Resend Link */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "13px",
                        color: "#667085",
                        marginBottom: "24px",
                        padding: "0 4px",
                    }}
                >
                    {timeLeft > 0 ? (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <AccessTimeIcon sx={{ fontSize: 16 }} /> Expires in: <strong style={{ color: "#e90000" }}>{formatTimer(timeLeft)}</strong>
                        </span>
                    ) : (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#ef4444", fontWeight: 700 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: "#ef4444" }} /> <strong style={{ color: "#ef4444" }}>OTP Expired</strong>
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={isResending}
                        style={{
                            background: "none",
                            border: "none",
                            color: isResending ? "#98a2b3" : "#e90000",
                            fontWeight: 700,
                            fontSize: "13px",
                            cursor: isResending ? "not-allowed" : "pointer",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            opacity: isResending ? 0.7 : 1,
                            transition: "all 0.2s",
                        }}
                    >
                        {isResending ? (
                            <>
                                <span style={{
                                    display: "inline-block",
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    border: "2px solid #98a2b3",
                                    borderTopColor: "transparent",
                                    animation: "spin 0.8s linear infinite",
                                }}></span>
                                Sending OTP...
                            </>
                        ) : (
                            "Resend OTP"
                        )}
                    </button>
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "12px" }}>
                    <button
                        type="button"
                        onClick={() => setOtpModal(prev => ({ ...prev, open: false, pendingToggle: null, error: "" }))}
                        style={{
                            flex: 1,
                            padding: "12px",
                            borderRadius: "10px",
                            border: "1px solid #d0d5dd",
                            background: "#ffffff",
                            color: "#344054",
                            fontWeight: 700,
                            fontSize: "14px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                        }}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={handleVerifyOtpAndSave}
                        disabled={otpModal.isSubmitting}
                        style={{
                            flex: 2,
                            padding: "12px",
                            borderRadius: "10px",
                            border: "none",
                            background: "#e90000",
                            color: "#ffffff",
                            fontWeight: 700,
                            fontSize: "14px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            transition: "all 0.2s",
                        }}
                    >
                        {otpModal.isSubmitting ? (
                            <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                            <>
                                <CheckCircleIcon sx={{ fontSize: 18 }} /> Verify & Apply Changes
                            </>
                        )}
                    </button>
                </div>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                TransitionComponent={SlideTransition}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
